'use client';

import {useEffect, useState} from 'react';
import {ContentType, searchableContentTypes} from './content_types';
import {BACKEND_URL} from 'config';

export type SearchResult = {
  title: string;
  description: string;
  url: string;
  content_type: ContentType;
};

type SearchResponse = {
  results: SearchResult[];
};

export function useSearchResults(query: string, contentTypes: ContentType[]) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contentTypesKey = contentTypes.join(',');

  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }

    const controller = new AbortController();
    const types = contentTypes.length ? contentTypes : searchableContentTypes;

    setIsLoading(true);
    setError(null);

    fetch(`${BACKEND_URL}/v1/magus/search`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({query, content_types: types, limit: 20}),
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Search error, please try again later.');
        return res.json() as Promise<SearchResponse>;
      })
      .then((data) => {
        setResults(data.results);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Search error, please try again later.');
        setIsLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, contentTypesKey]);

  return {results, isLoading, isError: Boolean(error), error: error ?? ''};
}
